import { Stack } from '@mui/material'
import { WarningIcon } from '@phosphor-icons/react'
import {
    Alert,
    ExtendedReportImage,
    IllustrationCard,
    MarketReportImage,
    MortgageReportImage,
    RevaluationReportImage,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { useState } from 'react'

const REPORT_TYPES = ['short_report', 'extended_report', 'revaluation_report'] as const
type ReportTypeSelectionValue = (typeof REPORT_TYPES)[number]
export type ExternalReportTypeSelectionValue = ReportTypeSelectionValue | 'benchmark_report'

type ReportTypeCardCopy = {
    title: string
    text?: string
}

export type ReportTypeSelectionCopy = {
    reportTypes?: Partial<Record<ExternalReportTypeSelectionValue, Partial<ReportTypeCardCopy>>>
}

const DEFAULT_REPORT_TYPE_SELECTION_COPY: {
    warningTitle: string
    reportTypes: Record<ExternalReportTypeSelectionValue, ReportTypeCardCopy>
} = {
    warningTitle: 'Achtung!',
    reportTypes: {
        short_report: { title: 'Markt- & Beleihungswert', text: 'gem. § 16 PfandBG' },
        extended_report: { title: 'Verkehrswert', text: 'gem. § 194 BauGB' },
        revaluation_report: { title: 'Folgebewertung', text: 'gem. § 194 BauGB' },
        benchmark_report: { title: 'Wertindikation' },
    },
}

const REPORT_TYPE_VISUALS = {
    short_report: {
        image: <MortgageReportImage height="100%" width="100%" />,
        hoverColor: tokens.ColorGraphSequentialSixLightest,
    },
    extended_report: {
        image: <ExtendedReportImage height="100%" width="100%" />,
        hoverColor: tokens.ColorGraphSequentialFourLightest,
    },
    revaluation_report: {
        image: <RevaluationReportImage height="100%" width="100%" />,
        hoverColor: tokens.ColorGraphSequentialTenLightest,
    },
    benchmark_report: {
        image: <MarketReportImage height="100%" width="100%" />,
        hoverColor: tokens.ColorGraphSequentialThreeLightest,
    },
}

type BaseReportTypeSelectionContentProps = {
    warning?: string
    isPending?: boolean
    copy?: ReportTypeSelectionCopy
}

export type ReportTypeSelectionContentProps = BaseReportTypeSelectionContentProps &
    (
        | {
              external?: false
              onSelect: (reportType: ReportTypeSelectionValue) => void
          }
        | { external: true; onSelect: (reportType: ExternalReportTypeSelectionValue) => void }
    )

export const ReportTypeSelectionContent = (props: ReportTypeSelectionContentProps) => {
    const { isPending, warning } = props
    const [chosenReportType, setChosenReportType] = useState<ExternalReportTypeSelectionValue | null>(null)

    const handleSelection = (reportType: ExternalReportTypeSelectionValue) => {
        setChosenReportType(reportType)
        if (props.external) {
            props.onSelect(reportType)
        } else if (reportType !== 'benchmark_report') {
            props.onSelect(reportType)
        }
    }

    return (
        <Stack direction="column" gap={tokens.SizingSpacing100} width="fit-content">
            {warning && (
                <Alert
                    size="small"
                    severity="error"
                    title={DEFAULT_REPORT_TYPE_SELECTION_COPY.warningTitle}
                    sx={{ maxWidth: '648px' }}
                    icon={<WarningIcon size={tokens.SizingIconM} color={tokens.ColorIconDanger} />}
                >
                    <Typography variant="textS" color={tokens.ColorTextBold}>
                        {warning}
                    </Typography>
                </Alert>
            )}
            <Stack direction="row" position="relative" gap={tokens.SizingSpacing300} height="280px">
                {[...REPORT_TYPES, ...(props.external ? (['benchmark_report'] as const) : [])].map((reportType) => (
                    <IllustrationCard
                        key={reportType}
                        {...getReportTypeConfiguration(reportType, props.copy?.reportTypes)}
                        isPending={isPending && chosenReportType === reportType}
                        disabled={isPending && chosenReportType !== reportType}
                        onClick={() => handleSelection(reportType)}
                    />
                ))}
            </Stack>
        </Stack>
    )
}

export function getReportTypeConfiguration(reportType: ExternalReportTypeSelectionValue, copy?: ReportTypeSelectionCopy['reportTypes']) {
    return {
        ...REPORT_TYPE_VISUALS[reportType],
        ...DEFAULT_REPORT_TYPE_SELECTION_COPY.reportTypes[reportType],
        ...copy?.[reportType],
    }
}
